import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Info, TrendingUp, TrendingDown } from 'lucide-react';

import { api } from '../lib/api.js';
import { useApiData } from '../lib/hooks.js';
import { useToast } from '../lib/store.js';
import { isoToday, fmtDate, relativeDay } from '../lib/format.js';
import {
  Card, Button, Sheet, Field, NumberInput, TextInput, TextArea, Choice,
  Spinner, ErrorNote, Pill, SectionTitle, PageHeader, EmptyState,
} from '../components/ui.jsx';

// Two jobs: log a migraine properly, and show what the app has noticed across
// them. The findings are counts, not conclusions, and the page says so more
// than once — with a handful of migraines, most apparent patterns are chance.

export default function Migraines() {
  const navigate = useNavigate();
  const list = useApiData('/migraines');
  const patterns = useApiData('/patterns');
  const [logging, setLogging] = useState(false);
  const { show } = useToast();

  if (list.loading && !list.data) return <Spinner label="Loading" />;
  if (list.error) return <div className="p-gutter"><ErrorNote error={list.error} onRetry={list.reload} /></div>;
  if (!list.data) return null;

  const { migraines, severityLabels, periodLabels, commonTriggers } = list.data;
  const p = patterns.data;

  const refresh = () => {
    list.reload({ quiet: true });
    patterns.reload({ quiet: true });
  };

  return (
    <div className="max-w-lg mx-auto">
      <PageHeader
        title="Migraines"
        subtitle="What you have logged, and what it has lined up with."
        back={() => navigate(-1)}
        right={
          <button onClick={() => setLogging(true)} className="p-2 text-accent" aria-label="Log a migraine">
            <Plus size={22} />
          </button>
        }
      />

      <div className="px-gutter">
        {migraines.length === 0 ? (
          <Card>
            <EmptyState
              title="Nothing logged yet"
              body="Log each one as it happens — severity, when it started, what you had tried. After a few, the app can start comparing them against your food, training, sleep and hydration."
              action={<Button onClick={() => setLogging(true)}>Log one</Button>}
            />
          </Card>
        ) : (
          <>
            {p?.summary?.count > 0 && (
              <Card className="mb-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-label uppercase text-textMute mb-1">Logged</p>
                    <p className="text-lg font-semibold">{p.summary.count}</p>
                  </div>
                  <div>
                    <p className="text-label uppercase text-textMute mb-1">Per month</p>
                    <p className="text-lg font-semibold">{p.summary.perMonth ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-label uppercase text-textMute mb-1">Avg severity</p>
                    <p className="text-lg font-semibold">{p.summary.averageSeverity ?? '—'}<span className="text-body-sm text-textMute">/5</span></p>
                  </div>
                </div>
                {p.summary.suspected?.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-edge">
                    <p className="text-label uppercase text-textMute mb-2">What you have suspected</p>
                    <div className="flex flex-wrap gap-1.5">
                      {p.summary.suspected.map((s) => (
                        <Pill key={s.name} tone="mute">{s.name} ×{s.count}</Pill>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            )}

            <SectionTitle>What the app has noticed</SectionTitle>
            {patterns.loading && !p && <Spinner />}

            {p && !p.ready && (
              <Card className="mb-4">
                <p className="text-body-sm text-textSoft">{p.reason}</p>
                <p className="text-meta text-textMute mt-2">
                  Logging steps, water, protein and how you slept is what gives this something to compare against.
                </p>
              </Card>
            )}

            {p?.ready && p.findings.length === 0 && (
              <Card className="mb-4">
                <p className="text-body-sm text-textSoft">
                  Nothing stood out above the noise across the {p.daysAnalysed} days checked. That is a real result rather than a gap —
                  it means none of training, fuelling, hydration, sleep or alcohol lined up with your migraines often enough to be worth
                  pointing at.
                </p>
              </Card>
            )}

            {p?.ready && p.findings.length > 0 && (
              <div className="space-y-2.5 mb-4">
                {p.findings.map((f) => (
                  <Card key={f.key}>
                    <div className="flex items-start gap-3">
                      {f.direction === 'more' ? (
                        <TrendingUp size={18} className="text-warn shrink-0 mt-0.5" />
                      ) : (
                        <TrendingDown size={18} className="text-good shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-body font-semibold mb-1">{f.label}</p>
                        <p className="text-body-sm text-textSoft">{f.sentence}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Pill tone={f.confidence === 'moderate' ? 'warn' : 'mute'}>{f.confidence} evidence</Pill>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {p?.ready && p.foods && (
              <Card className="mb-4">
                <SectionTitle>Food</SectionTitle>
                <p className="text-body-sm text-textSoft">{p.foods.note}</p>
                {p.foods.items?.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {p.foods.items.map((f) => (
                      <li key={f.name} className="text-body-sm text-textSoft flex gap-2">
                        <span className="text-accent shrink-0">·</span>
                        {f.sentence}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            )}

            {p?.caveat && (
              <Card className="mb-5 bg-panelHi">
                <div className="flex gap-3">
                  <Info size={18} className="text-textMute shrink-0 mt-0.5" />
                  <p className="text-meta text-textMute">{p.caveat}</p>
                </div>
              </Card>
            )}

            <SectionTitle>History</SectionTitle>
            <ul className="space-y-2 mb-8">
              {migraines.map((m) => (
                <li key={m.id}>
                  <Card>
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-body font-semibold">{relativeDay(m.date)}</span>
                          <Pill tone={m.severity >= 4 ? 'crit' : m.severity >= 3 ? 'warn' : 'mute'}>
                            {severityLabels[m.severity]}
                          </Pill>
                        </div>
                        <p className="text-meta text-textMute mt-1">
                          {[
                            m.started_period ? periodLabels[m.started_period] : null,
                            m.duration_hours ? `${m.duration_hours}h` : null,
                            m.aura ? 'aura' : null,
                            m.nausea ? 'nausea' : null,
                            m.light_sound ? 'light/sound' : null,
                          ].filter(Boolean).join(' · ') || fmtDate(m.date)}
                        </p>
                        {m.suspected && <p className="text-meta text-textSoft mt-1.5">Suspected: {m.suspected}</p>}
                        {m.what_helped && <p className="text-meta text-textSoft">Helped: {m.what_helped}</p>}
                        {m.note && <p className="text-body-sm text-textSoft mt-1.5">{m.note}</p>}
                      </div>
                      <button
                        onClick={async () => {
                          await api.del(`/migraines?id=${m.id}`);
                          show('Removed.');
                          refresh();
                        }}
                        className="p-1.5 -mr-1 text-textMute hover:text-crit shrink-0"
                        aria-label={`Delete migraine logged on ${fmtDate(m.date)}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </Card>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <MigraineSheet
        open={logging}
        onClose={() => setLogging(false)}
        onSaved={() => { setLogging(false); refresh(); }}
        severityLabels={severityLabels}
        periodLabels={periodLabels}
        commonTriggers={commonTriggers}
      />
    </div>
  );
}

export function MigraineSheet({ open, onClose, onSaved, severityLabels, periodLabels, commonTriggers, date }) {
  const { show } = useToast();
  const [form, setForm] = useState({ date: date || isoToday(), severity: 3, suspected: [] });
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleTrigger = (t) =>
    set({ suspected: form.suspected.includes(t) ? form.suspected.filter((x) => x !== t) : [...form.suspected, t] });

  // Defaults matter: this sheet is also opened straight from Today, where the
  // lists have not been fetched. They mirror the server's own values.
  const labels = severityLabels || { 1: 'Niggle', 2: 'Mild', 3: 'Moderate', 4: 'Severe', 5: "Couldn't function" };
  const periods = periodLabels || {
    woke_with: 'Woke up with it', morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening', night: 'Night',
  };
  const triggers = commonTriggers || [
    'Not eaten enough', 'Skipped a meal', 'Dehydrated', 'Poor sleep', 'Stress',
    'Alcohol', 'Hormonal', 'Screen time', 'Weather', 'Exercise', 'Not sure',
  ];

  return (
    <Sheet open onClose={onClose} title="Log a migraine">
      <Field label="When" id="mdate">
        <TextInput id="mdate" type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} max={isoToday()} />
      </Field>

      <Field label="How bad?" id="sev" hint="Roughly. It only needs to be consistent enough to compare with the others.">
        <div className="space-y-1.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => set({ severity: n })}
              aria-pressed={form.severity === n}
              className={`w-full flex items-center gap-3 p-3 rounded-control border text-left transition-colors ${
                form.severity === n ? 'border-accent bg-accentSoft' : 'border-edge hover:border-edgeHi'
              }`}
            >
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-body-sm font-semibold shrink-0 ${
                form.severity === n ? 'bg-accent text-onAccent' : 'bg-panelHi text-textMute'
              }`}>
                {n}
              </span>
              <span className="text-body-sm">{labels[n]}</span>
            </button>
          ))}
        </div>
      </Field>

      <Field label="When did it start?" id="per">
        <Choice
          columns={2}
          value={form.started_period}
          onChange={(v) => set({ started_period: v })}
          options={Object.entries(periods).map(([value, label]) => ({ value, label }))}
        />
      </Field>

      <Field label="How long did it last?" id="dur" hint="Leave blank if it is still going.">
        <NumberInput id="dur" suffix="hrs" step="0.5" value={form.duration_hours ?? ''} onChange={(e) => set({ duration_hours: e.target.value })} />
      </Field>

      <Field label="Symptoms" id="sym">
        <div className="space-y-2">
          {[
            ['aura', 'Aura or visual disturbance'],
            ['nausea', 'Nausea or sickness'],
            ['light_sound', 'Light or sound sensitivity'],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-3 p-3 rounded-control border border-edge cursor-pointer">
              <input
                type="checkbox"
                checked={!!form[key]}
                onChange={(e) => set({ [key]: e.target.checked })}
                className="w-4 h-4 accent-[var(--c-accent)]"
              />
              <span className="text-body-sm">{label}</span>
            </label>
          ))}
        </div>
      </Field>

      <Field label="What do you think brought it on?" id="trig" hint="Your hunch, kept separate from anything the app works out. Tap as many as apply.">
        <div className="flex flex-wrap gap-1.5">
          {triggers.map((t) => {
            const on = form.suspected.includes(t);
            return (
              <button
                key={t}
                type="button"
                onClick={() => toggleTrigger(t)}
                aria-pressed={on}
                className={`px-3 py-2 rounded-full border text-body-sm transition-colors ${
                  on ? 'bg-accent text-onAccent border-accent' : 'bg-panel text-textSoft border-edge'
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="What helped?" id="helped" hint="Worth recording — it is the bit you will forget by next time.">
        <TextInput id="helped" value={form.what_helped ?? ''} onChange={(e) => set({ what_helped: e.target.value })} placeholder="e.g. food and a lie down, sugary drink" />
      </Field>

      <Field label="Anything you took?" id="pain">
        <TextInput id="pain" value={form.painkillers ?? ''} onChange={(e) => set({ painkillers: e.target.value })} placeholder="e.g. paracetamol" />
      </Field>

      <Field label="Anything else" id="mnote">
        <TextArea id="mnote" rows={2} value={form.note ?? ''} onChange={(e) => set({ note: e.target.value })} />
      </Field>

      <Button
        className="w-full"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await api.post('/migraines', form);
            show('Logged. Hope it passes quickly.');
            onSaved();
          } catch (err) {
            show(err.message, 'crit');
          } finally {
            setBusy(false);
          }
        }}
      >
        Save
      </Button>
    </Sheet>
  );
}
